/**
 * TODO: refactor component to use TextFormField
 */
import { useContext, useEffect } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { AdornedInput, FormField, FormHelp, Label, TextButton } from '@zillow/constellation-10';

import { cloneDeep } from 'es-toolkit';
import {
  propertyUtils,
  useRentalManagerUserPropertyEditWebApiHandler_securityDeposit,
} from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import WizardServerError from '../../../../model/WizardServerError';

import PropertiesActions from '../../../../actions/PropertiesActions';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import numberUtils from '../../../../utils/numberUtils';
import { AppSelectors } from '../../../../selectors/index';
import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks/index';

import { postingConstraints } from '../../../../utils/formValidation';

import actions from '../../../../containers/CreateListingPage/actions';

import wizardActions from '../../../Wizard/actions';

import WizardAlert from '../../../WizardAlert/index';

import type { ChangeEvent } from 'react';

const propTypes = {};
const defaultProps = {};

const inputName = 'securityDeposit';

const SecurityDepositFormField = ({ showOneMonthsRentButton = true }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const { floorplans, currentProperty } = useSelector(
    (state) => ({
      floorplans: state.properties.currentProperty.floorplans,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const { mutateAsync: updateSecurityDeposit } = useRentalManagerUserPropertyEditWebApiHandler_securityDeposit();

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const floorplan = floorplans[0] || {};
  const { price, securityDeposit } = floorplan;

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const formLabel = isRoomForRent ? `Renter’s security deposit` : 'Security deposit';

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      securityDeposit,
    },
    postingConstraints,
  );

  // this is called in the sd modal in the Review step when the user clicks 'save'
  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error
          value: inputs.securityDeposit,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = async (event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } }) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    const listingAlias = currentProperty?.listingAlias;

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    try {
      if (!listingAlias) {
        logError({
          errorType: 'ListingAliasMissingError',
          error: new Error('Missing listingAlias'),
          context: `SecurityDepositFormField.save: fieldName: ${fieldName}, currentProperty: ${JSON.stringify(currentProperty)}`,
        });
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }

      const response = await updateSecurityDeposit({
        getParams: {
          listingAlias,
          securityDeposit: Number(value),
        },
      });

      const { isSecurityDepositWaived } = response || {};

      const copyFloorplan = cloneDeep(floorplans);
      copyFloorplan[0][fieldName] = value;

      queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      dispatch(PropertiesActions.updateCurrentProperty({ floorplans: copyFloorplan }));
      dispatch(PropertiesActions.updateCurrentProperty({ securityDeposit: value }));
      dispatch(PropertiesActions.updateCurrentPropertyRentalProtection({ isSecurityDepositWaived }));

      // reset save cta
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      updateServerError(errorMessage, fieldName);
    } finally {
      wizardActions.updateIsFetchingData({
        reducerWizardDispatch,
        isFetchingData: false,
      });
    }
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  const handleRequireOneMonthsRentClick = () => {
    const mockEvent = {
      target: {
        name: inputName,
        value: price,
      },
    };

    handleFormChange(mockEvent);
    save(mockEvent);
  };

  return (
    <>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      {/* this is  needed in the sd modal in the Review step to match the figma design */}

      <FormField
        data-testid="SecurityDepositFormField"
        label={<Label htmlFor="security-deposit-input">{formLabel}</Label>}
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                name={inputName}
                value={String(inputs.securityDeposit || '')}
                placeholder=""
                onChange={handleChange}
                onBlur={save}
                id="security-deposit-input"
                aria-describedby="security-deposit-description"
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
          />
        }
        // @ts-expect-error
        error={!!errorObj.securityDeposit}
        description={
          // @ts-expect-error
          <FormHelp id="security-deposit-description" error={!!errorObj.securityDeposit}>
            {/* @ts-expect-error */}
            {errorObj.securityDeposit}
          </FormHelp>
        }
      >
        {price && showOneMonthsRentButton && (
          <TextButton
            tone="brand"
            onClick={handleRequireOneMonthsRentClick}
            data-testid="SecurityDepositFormField-SetDepositAsOneMonthsRentButton"
            css={{ marginTop: '600' }}
          >
            Set deposit as {numberUtils.asCurrency(price)} (one month’s rent)
          </TextButton>
        )}
      </FormField>
    </>
  );
};

SecurityDepositFormField.propTypes = propTypes;
SecurityDepositFormField.defaultProps = defaultProps;

export default SecurityDepositFormField;
